import {
	capitalizeFirstLetter,
	NETWORK_RECIPIENTS_LABELS,
	noop,
	type RecipientItem,
	truncateString,
	useIntl
} from '@capsicum/common';
import { useTheme } from 'styled-components';

import { ListCard, NetworkIcon } from '../../atoms';
import { EditSquareIcon, GoatAvatar } from '../../icons';
import { RadioButtonInput } from '../form/radio-group-input/RadioButtonInput';

import { S } from './RecipientCard.styled';

export type RecipientCardProps = {
	interactive?: boolean;
	onEditRecipient?: () => void;
	onSelectRecipient: (recipient: RecipientItem) => void;
	recipient?: RecipientItem;
	selectedRecipient?: Pick<RecipientItem, 'id'>;
	testID?: string;
};

export const RecipientCard = ({
	interactive = true,
	onEditRecipient,
	onSelectRecipient,
	recipient,
	selectedRecipient: { id: selectedRecipientId } = { id: '' },
	testID = 'recipient-card'
}: RecipientCardProps) => {
	const { $t } = useIntl();
	const { colors } = useTheme();

	if (!recipient || !onEditRecipient || !onSelectRecipient) {
		return;
	}

	return (
		<S.Container
			$interactive={interactive}
			$selected={selectedRecipientId === recipient.id}
			testID={testID + '-' + recipient.id}
			onClick={() => {
				interactive && onSelectRecipient(recipient);
			}}
		>
			<S.Content $interactive={interactive}>
				<S.ContentTop>
					<GoatAvatar
						color={colors.icons.main.enabled}
						data-testid={testID + '-avatar-' + recipient.id}
						onClick={
							interactive
								? () => {
										interactive && onSelectRecipient(recipient);
									}
								: undefined
						}
					/>
					<S.TitleWrapper>
						<S.TitleContainer>
							<S.ListCardTitle>{recipient.label}</S.ListCardTitle>
							{interactive && (
								<EditSquareIcon
									color={colors.icons.main.enabled}
									data-testid={testID + '-editButton-' + recipient.id}
									onClick={e => {
										e.stopPropagation();
										onEditRecipient();
									}}
								/>
							)}
						</S.TitleContainer>
						<ListCard.SubTitle>{capitalizeFirstLetter(recipient.type)}</ListCard.SubTitle>
					</S.TitleWrapper>
				</S.ContentTop>
				<S.NetworkDivider orientation="horizontal" />
				<div>
					<S.Network>
						<NetworkIcon network={recipient.networkType} size="small" variant="circle" />
						<S.NetworkLabel variant="caption1">
							{recipient.networkType && NETWORK_RECIPIENTS_LABELS[recipient.networkType]}
						</S.NetworkLabel>
						<S.NetworkExchange color="main.subtle" variant="caption2">
							{recipient?.exchange}
						</S.NetworkExchange>
					</S.Network>
					<div>
						<S.Address color="main.subtle" variant="text3">
							{$t('withdrawal.recipientList.address', {
								blockchainAddress: truncateString(recipient.blockchainAddress, 8, 8)
							})}
						</S.Address>
					</div>
				</div>
			</S.Content>
			{interactive && (
				<S.End>
					<RadioButtonInput
						checked={selectedRecipientId === recipient.id}
						testID={testID + '-radioButton-' + recipient.id}
						onPress={noop}
					/>
				</S.End>
			)}
		</S.Container>
	);
};
